export type CategoryId = "flower" | "preRolls" | "edibles" | "specials";

export type QuantityPrice = { qty: string; price: string };

export type ProductDetails = {
  type?: string;
  aroma?: string;
  flavor?: string;
  effect?: string;
  genetics?: string;
  helps?: string;
  terpenes?: string;
  deals?: string[];
};

export type ProductPlaceholder = {
  name: string;
  slug: string;
  tiers: QuantityPrice[];
  imageUrl?: string;
  details?: ProductDetails;
  /** Defaults to true when omitted. Set to false to show an "Out of Stock" badge. */
  available?: boolean;
  /** When present, a flavor must be selected before adding to cart. */
  flavors?: string[];
};

export type FlowerSection = {
  title: string;
  strains: ProductPlaceholder[];
};

export const CATALOG_TABS: { id: CategoryId; label: string }[] = [
  { id: "flower", label: "Flower" },
  { id: "preRolls", label: "Pre-rolls" },
  { id: "edibles", label: "Edibles" },
  { id: "specials", label: "Specials" },
];

const flowerTiers = (a: string, b: string, c: string): QuantityPrice[] => [
  { qty: "1/4 oz", price: a },
  { qty: "1/2 oz", price: b },
  { qty: "1 oz", price: c },
];

export const FLOWER_SECTIONS: FlowerSection[] = [
  {
    title: "Boutique",
    strains: [
      {
        name: "Didi Oil",
        slug: "didi-oil",
        tiers: flowerTiers("$60", "$120", "$200"),
        imageUrl: "/products/didi-oil.webp",
        details: {
          type: "Hybrid",
          aroma: "fuel, pine, citrus",
          flavor: "diesel, lemon zest, earth",
          effect: "euphoric, creative, uplifted",
          genetics: "OG Kush x Sour Diesel",
          helps: "stress, fatigue, depression",
          terpenes: "limonene, myrcene, caryophyllene",
          deals: ["BUY 2 oz's SAVE $50", "BUY 3 oz's SAVE $150"],
        },
      },
      {
        name: "Rock Candy",
        slug: "rock-candy",
        tiers: flowerTiers("$60", "$120", "$200"),
        imageUrl: "/products/rock-candy.webp",
        details: {
          type: "Sativa-dominant Hybrid",
          aroma: "sweet, candy, floral",
          flavor: "sugar, berry, vanilla",
          effect: "energetic, happy, focused",
          genetics: "Pineapple x Rock Bud",
          helps: "fatigue, depression, lack of appetite",
          terpenes: "terpinolene, limonene, ocimene",
          deals: ["BUY 2 oz's SAVE $50", "BUY 3 oz's SAVE $150"],
        },
      },
      {
        name: "Lemon Cherry Gelato",
        slug: "lemon-cherry-gelato",
        tiers: flowerTiers("$60", "$120", "$200"),
        imageUrl: "/products/lemon-cherry-gelato.webp",
        details: {
          type: "Hybrid",
          aroma: "lemon, cherry, cream",
          flavor: "lemon zest, sweet cherry, gelato cream",
          effect: "relaxed, euphoric, giggly",
          genetics: "Lemon Cherry Gelato phenotype",
          helps: "anxiety, pain, mood swings",
          terpenes: "limonene, linalool, caryophyllene",
          deals: ["BUY 2 oz's SAVE $50", "BUY 3 oz's SAVE $150"],
        },
      },
      {
        name: "Molotov Cocktail",
        slug: "molotov-cocktail",
        tiers: flowerTiers("$60", "$120", "$200"),
        imageUrl: "/products/molotov-cocktail.webp",
        details: {
          type: "Indica-dominant Hybrid",
          aroma: "gas, tropical, spice",
          flavor: "fuel, mango, pepper",
          effect: "heavy, relaxed, sedating",
          genetics: "Jet Fuel Gelato x Papaya",
          helps: "insomnia, chronic pain, stress",
          terpenes: "myrcene, caryophyllene, humulene",
          deals: ["BUY 2 oz's SAVE $50", "BUY 3 oz's SAVE $150"],
        },
      },
    ],
  },
  {
    title: "Indoor",
    strains: [
      {
        name: "The Dream",
        slug: "the-dream",
        tiers: flowerTiers("$50", "$100", "$170"),
        imageUrl: "/products/the-dream.webp",
        details: {
          type: "Sativa-dominant Hybrid",
          aroma: "blueberry, sweet, herbal",
          flavor: "berry, vanilla, sugar",
          effect: "euphoric, creative, uplifted",
          genetics: "Blue Dream phenotype",
          helps: "pain, depression, nausea",
          terpenes: "myrcene, pinene, caryophyllene",
        },
      },
      {
        name: "Citral Glue",
        slug: "citral-glue",
        tiers: flowerTiers("$50", "$100", "$170"),
        imageUrl: "/products/citral-glue.webp",
        details: {
          type: "Hybrid",
          aroma: "chemical, pine, earth",
          flavor: "diesel, pine, chocolate",
          effect: "heavy, relaxed, euphoric",
          genetics: "Gorilla Glue #4 phenotype",
          helps: "chronic pain, insomnia, stress",
          terpenes: "caryophyllene, myrcene, limonene",
        },
      },
      {
        name: "Sour Diesel x Runts",
        slug: "sour-diesel-x-runts",
        tiers: flowerTiers("$50", "$100", "$170"),
        imageUrl: "/products/sour-diesel-x-runts.webp",
        details: {
          type: "Sativa-dominant Hybrid",
          aroma: "fuel, citrus, candy",
          flavor: "sour diesel, sweet runtz, tropical",
          effect: "energetic, creative, euphoric",
          genetics: "Sour Diesel x Runtz",
          helps: "fatigue, depression, stress",
          terpenes: "limonene, terpinolene, caryophyllene",
        },
      },
      {
        name: "Curelato",
        slug: "curelato",
        tiers: flowerTiers("$50", "$100", "$170"),
        imageUrl: "/products/curelato.webp",
        details: {
          type: "Hybrid",
          aroma: "sweet, creamy, earthy",
          flavor: "gelato cream, mint, cookie",
          effect: "relaxed, happy, calm",
          genetics: "Gelato x Biscotti",
          helps: "anxiety, pain, appetite loss",
          terpenes: "linalool, limonene, caryophyllene",
        },
      },
    ],
  },
  {
    title: "AAA",
    strains: [
      {
        name: "Hippy Crippler",
        slug: "hippy-crippler",
        tiers: flowerTiers("$40", "$60", "$100"),
        imageUrl: "/products/hippy-crippler.webp",
        details: {
          type: "Sativa-dominant Hybrid",
          aroma: "sweet, earthy, floral",
          flavor: "herbal, sweet, pine",
          effect: "uplifting, creative, relaxed",
          genetics: "AK-47 x Blue Satellite",
          helps: "depression, fatigue, stress",
          terpenes: "myrcene, terpinolene, pinene",
        },
      },
    ],
  },
];

export const PLACEHOLDERS: Record<Exclude<CategoryId, "flower">, ProductPlaceholder[]> = {
  preRolls: [
    {
      name: "Kief Infused Boutique Pre-Roll",
      slug: "kief-infused-boutique-preroll",
      tiers: [
        { qty: "1", price: "$10" },
        { qty: "3-pack", price: "$26" },
        { qty: "5-pack", price: "$42" },
      ],
      imageUrl: "/products/preroll-hero-1.png",
      details: {
        type: "Hybrid",
        aroma: "floral, kief-coated, sweet",
        flavor: "smooth, creamy, herbal",
        effect: "elevated, relaxed, euphoric",
      },
    },
    {
      name: "Perfect Pre-Rolls",
      slug: "perfect-pre-rolls",
      tiers: [
        { qty: "1", price: "$14" },
        { qty: "3-pack", price: "$36" },
        { qty: "5-pack", price: "$56" },
      ],
      imageUrl: "/products/preroll-hero-2.png",
      details: {
        type: "Hybrid",
        aroma: "fresh herb, pine",
        flavor: "clean, herbal, citrus",
        effect: "balanced, smooth, social",
      },
    },
    {
      name: "Mini Dogwalker",
      slug: "mini-dogwalker",
      tiers: [
        { qty: "1", price: "$8" },
        { qty: "5-pack", price: "$35" },
        { qty: "10-pack", price: "$64" },
      ],
      imageUrl: "/products/preroll-hero-1.png",
      details: {
        type: "Hybrid",
        aroma: "light, herbal",
        flavor: "mild, clean",
        effect: "light buzz, functional, quick",
      },
    },
    {
      name: "King 1g Cone",
      slug: "king-1g-cone",
      tiers: [
        { qty: "1", price: "$16" },
        { qty: "2-pack", price: "$30" },
      ],
      imageUrl: "/products/preroll-hero-2.png",
      details: {
        type: "Hybrid",
        aroma: "strong, pungent, gassy",
        flavor: "full-bodied, fuel, earth",
        effect: "heavy, long-lasting, sedating",
      },
    },
  ],
  edibles: [
    {
      name: "25mg Nano Gummies",
      slug: "25mg-nano-gummies",
      tiers: [
        { qty: "100 mg pkg", price: "$24" },
        { qty: "200 mg pkg", price: "$42" },
      ],
      imageUrl: "/products/edible-hero-1.png",
      flavors: ["Lime", "Peach", "Watermelon", "Orange", "Variety Pack"],
      details: {
        type: "Edible — Nano Gummy",
        flavor: "lime, peach, watermelon, orange",
        effect: "fast-acting, clear-headed, uplifting",
        helps: "stress, mood, social anxiety",
      },
    },
  ],
  specials: [
    {
      name: "Weekly Bundle",
      slug: "weekly-bundle",
      tiers: [
        { qty: "each", price: "$39" },
        { qty: "2 for", price: "$72" },
      ],
      imageUrl: "/products/boutique-hero-1.png",
    },
    {
      name: "Seasonal Promo",
      slug: "seasonal-promo",
      tiers: [
        { qty: "while supplies last", price: "$28" },
        { qty: "2-pack", price: "$50" },
      ],
      imageUrl: "/products/indoor-hero-3.png",
    },
  ],
};

/** Find any product by slug across all categories. */
export function findProductBySlug(slug: string): ProductPlaceholder | undefined {
  for (const section of FLOWER_SECTIONS) {
    const found = section.strains.find((p) => p.slug === slug);
    if (found) return found;
  }
  for (const cat of Object.values(PLACEHOLDERS)) {
    const found = cat.find((p) => p.slug === slug);
    if (found) return found;
  }
  return undefined;
}
